import { SideMenuHr, uiTextCapitalize } from '@metrika/ui-kit';
import { useThemeValue } from '@metrika/ui-kit/build/theme/useThemeValue';
import { SideNavMenuItem } from '@metrika/ui-kit/carbon';
import { vanillaCss } from '@metrika/ui-kit/emotion';
import { Launch } from '@metrika/ui-kit/icons/carbon';
import React, { useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Redirect, Route, useLocation } from 'react-router-dom';
import { appEnv, useFlags } from '../../../_env';
import { Assets } from '../../../_risk/_static/assets';
import { RiskAnalyzeIcon, RiskHomeIcon, RiskSettingsIcon } from '../../../_risk/_static/icons/icons';
import { RiskDiscoveryMonitorSidebar } from '../../../_risk/RiskDiscoveryMonitorSidebar';
import { riskBasePath } from '../../../_shared';
import { useSidebarDashboards, useIsLogged } from '../../../user/hooks';
import { SidebarWorkspace } from '../../../user/types';
import { CarbonNavSubLink, SideNavMenuFixed } from './components';
import { MenuDashboards } from './MenuDashboards';

const docsMenuClass = vanillaCss`
   .cds--side-nav__link-text {
      width: 100%;
   }
`;
export const RenderProtocolIconSide = (props: { protocol: string; isSearch?: boolean }) => {
   const { theme } = useThemeValue();
   const asset = Assets.find((a) => a.id === props.protocol.toLowerCase());
   const invert = theme === 'dark' && asset?.noInvert !== true;
   if (!asset) return <></>;

   return (
      <div
         style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: props.isSearch ? 'default' : 'pointer',
         }}
      >
         <img
            src={asset.icon_url}
            alt={asset.title}
            style={{
               width: '16px',
               height: '16px',
               filter: invert ? 'invert(1)' : 'none',
               marginLeft: props.isSearch ? '0' : '1.2rem',
            }}
         />
      </div>
   );
};
const SidebarMainMenu_: React.FC<{
   onToggle: () => void;
   workspace: SidebarWorkspace;
}> = ({ onToggle, workspace }) => {
   const { data: allDashMenus } = useSidebarDashboards();
   const { pathname } = useLocation();
   const { isMetrikaUser } = useIsLogged();
   const flags = useFlags();

   const urlMatch = useCallback(
      (m: string | undefined) => (m ? pathname.toLowerCase().indexOf(m.toLowerCase()) === 0 : false),
      [pathname],
   );

   return (
      <>
         {/* Analyze v3 menu - only for Metrika users */}
         {flags.showV3Features && isMetrikaUser && (
            <SideNavMenuFixed
               key="analyze-v3"
               title="Analyze"
               renderIcon={RiskAnalyzeIcon}
               defaultExpanded={urlMatch(`${riskBasePath}/analyze-v3`)}
            >
               <CarbonNavSubLink
                  to={`${riskBasePath}/analyze-v3`}
                  title="Dashboards"
                  onClick={onToggle}
                  isActive={urlMatch(`${riskBasePath}/analyze-v3`)}
               />
            </SideNavMenuFixed>
         )}

         {workspace.children?.map((menu) => {
            if (menu.kind === 'simple-menu' && menu.id === 'risk-analyze') {
               return (
                  <SideNavMenuFixed
                     key={menu.id}
                     title={menu.title}
                     renderIcon={RiskAnalyzeIcon}
                     defaultExpanded={urlMatch(menu.urlMatch)}
                  >
                     {allDashMenus?.map((w) => (
                        <React.Fragment key={w.id}>
                           <SideNavMenuFixed
                              indent={0}
                              key={w.id}
                              renderIcon={() => <RenderProtocolIconSide protocol={w.id} />}
                              title={w.title}
                              defaultExpanded={urlMatch(`${riskBasePath}/analyze/${w.id}`)}
                           >
                              {w.dashMenus.map((menu) => {
                                 return (
                                    <React.Fragment key={menu.id}>
                                       <SideNavMenuFixed
                                          indent={3}
                                          key={menu.id}
                                          title={menu.title}
                                          defaultExpanded={urlMatch(`${riskBasePath}/analyze${menu.urlMatch}`)}
                                       >
                                          <MenuDashboards
                                             onToggle={onToggle}
                                             workspaceId={w.id}
                                             menuId={menu.menuId}
                                             pathPrefix={`${riskBasePath}/analyze`}
                                          />
                                       </SideNavMenuFixed>
                                    </React.Fragment>
                                 );
                              })}
                           </SideNavMenuFixed>
                        </React.Fragment>
                     ))}
                  </SideNavMenuFixed>
               );
            }
            if (menu.kind === 'simple-menu' && menu.id === 'risk-monitor-new') {
               return (
                  <RiskDiscoveryMonitorSidebar
                     key={'risk-monitor-sidebar'}
                     menu={menu}
                     onToggle={onToggle}
                     urlMatch={urlMatch}
                  />
               );
            }
            if (menu.kind === 'simple-menu') {
               return (
                  <SideNavMenuFixed
                     key={menu.id}
                     title={menu.title}
                     renderIcon={
                        menu.id === 'risk-home'
                           ? RiskHomeIcon
                           : menu.id === 'risk-settings'
                             ? RiskSettingsIcon
                             : undefined
                     }
                     defaultExpanded={urlMatch(menu.urlMatch)}
                  >
                     {menu.children?.map((submenu) => {
                        // For Settings submenu: rewrite Organization and My Profile
                        // to point at v3 (risk-next) members and profile.
                        const riskNextUrl =
                           menu.id === 'risk-settings' && submenu.url?.includes('/settings/org')
                              ? `${appEnv.appDomains.riskNext}/risk/settings/members`
                              : menu.id === 'risk-settings' && submenu.url?.includes('/settings/user')
                                ? `${appEnv.appDomains.riskNext}/risk/settings/profile`
                                : null;
                        return (
                           <React.Fragment key={submenu.id}>
                              <Route path={submenu.url}>
                                 <Helmet>
                                    <title>
                                       {uiTextCapitalize(workspace.id)} {submenu.title}
                                    </title>
                                    <meta name={'description'} content={submenu.title} />
                                 </Helmet>
                              </Route>
                              {riskNextUrl ? (
                                 <SideNavMenuItem as="a" href={riskNextUrl}>
                                    {submenu.title}
                                 </SideNavMenuItem>
                              ) : (
                                 <CarbonNavSubLink
                                    to={submenu.url || '#'}
                                    title={submenu.title}
                                    onClick={onToggle}
                                    isActive={urlMatch(submenu.urlMatch)}
                                 />
                              )}
                           </React.Fragment>
                        );
                     })}
                     {Array.isArray(menu.children) &&
                        menu.children.length > 0 &&
                        menu.urlMatch &&
                        menu.children[0].url && (
                           <Route path={menu.urlMatch} exact>
                              <Redirect to={menu.children[0].url} />
                           </Route>
                        )}
                  </SideNavMenuFixed>
               );
            }
            if (menu.kind === 'docs-link') {
               return (
                  <React.Fragment key={menu.id}>
                     {menu.id !== 'risk-about' && <SideMenuHr />}
                     <SideNavMenuItem
                        as={'a'}
                        href={menu.url || '#'}
                        target={'_blank'}
                        rel={'opener referrer'}
                        className={docsMenuClass}
                     >
                        <div
                           style={{
                              justifyContent: 'space-between',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              width: '100%',
                           }}
                        >
                           <span>{menu.title}</span> <Launch size={16} />
                        </div>
                     </SideNavMenuItem>
                  </React.Fragment>
               );
            }
            return null;
         })}
      </>
   );
};

export const SidebarMainMenu = React.memo(SidebarMainMenu_);
